import type {NextSlotDto, QueueCountersDto} from '@devmil/contracts';

import {formatTimeOfDay, KIND_LABEL} from '../../lib/format.js';

const SLOT_STATUS_LABEL = {valid: 'válido', expired: 'vencido'} as const;

/**
 * A legenda do contador de publicação, e por que ela conta o que deu errado.
 *
 * Um "No ar: 3" sozinho não distingue um dia com quatro peças ainda agendadas
 * de um dia com quatro peças que falharam. As duas situações pedem coisas
 * opostas de quem lê: a primeira é esperar, e a segunda é ir olhar.
 *
 * O que NÃO deu certo vem primeiro, quando existe: é a informação que muda a
 * ação. Dia limpo mostra quantas ainda estão por vir, e o dia inteiro no ar
 * mostra que acabou.
 */
const legendaDaPublicacao = (counters: QueueCountersDto): string => {
  const problemas = counters.failed + counters.expired;
  if (problemas > 0) {
    return `${counters.published} no ar · ${problemas} sem publicar`;
  }
  return `${counters.published} no ar`;
};

/**
 * Quantas peças o dono LIBEROU hoje, em qualquer estado de publicação.
 *
 * A soma dos cinco, e não `counters.released` sozinho: aquele contador é
 * "liberada e ainda sem item na fila de publicação", que é um estado de
 * passagem de minutos. Mostrá-lo sozinho faria o número CAIR quando a
 * publicação avançasse, e um contador que diminui porque as coisas deram certo
 * é um contador que ninguém acredita.
 */
const liberadasDoDia = (counters: QueueCountersDto): number =>
  counters.released +
  counters.scheduled +
  counters.published +
  counters.expired +
  counters.failed;

const Stat = ({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption: string;
}) => (
  <div className="dm-stat">
    <span>{label}</span>
    <strong>{value}</strong>
    <code>{caption}</code>
  </div>
);

export const StatGrid = ({
  counters,
  nextSlot,
}: {
  counters: QueueCountersDto;
  nextSlot: NextSlotDto | null;
}) => (
  <div className="dm-stats" role="group" aria-label="Resumo diário">
    <Stat
      label="Peças geradas"
      value={String(counters.generated)}
      /*
        A legenda dizia "meta diária completa", e essa frase era lida como prova
        de publicação (achado F12 da auditoria de 06/09/2026). Peça GERADA é
        peça que a esteira escreveu: ela pode estar esperando decisão, pode ter
        sido rejeitada, pode ter vencido o horário sem ir ao ar. Num dia em que
        a fila de publicação falhasse inteira, o painel continuaria dizendo
        "meta diária completa".

        Quem prova publicação é o contador "No ar", ao lado.
      */
      caption="escritas pela esteira"
    />
    <Stat
      label="Aguardando"
      value={String(counters.awaiting)}
      caption="decisão humana"
    />
    <Stat
      label="Liberadas"
      /*
        O que o DONO liberou hoje, some o que a publicação fez com isso: a peça
        que ele aprovou continua sendo dele mesmo depois de ir ao ar. Somar os
        cinco estados de publicação é o que mantém este número igual ao que a
        tela sempre mostrou, e é o que faz o clique de aprovar mover dois
        números, como movia.
      */
      value={String(liberadasDoDia(counters))}
      // A legenda é a que MUDOU, e ela é a correção do achado F12: "manifesto
      // selado" descrevia o selo, e o dono precisa saber quantas foram ao ar.
      caption={legendaDaPublicacao(counters)}
    />
    <Stat
      label="Próximo slot"
      // Sem próximo slot o painel diz que não há, e não finge um horário.
      value={nextSlot ? formatTimeOfDay(nextSlot.scheduledAt) : 'Indisp.'}
      caption={
        nextSlot
          ? `${KIND_LABEL[nextSlot.kind]} · ${SLOT_STATUS_LABEL[nextSlot.status]}`
          : 'sem slot programado'
      }
    />
  </div>
);
